import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge"; // Added Badge import
import { Star, Video, Headphones, BookOpen, Trophy, Crown, Lock } from 'lucide-react';
import { LessonCardHeader } from './lesson/LessonCardHeader';
import { useToast } from "@/hooks/use-toast";
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "../hooks/useAuth";

interface LessonCardProps {
  id: string;
  title: string;
  description: string;
  points: number;
  isLocked?: boolean;
  imageUrl?: string;
  videoUrl?: string;
  onStart?: () => void;
  isPremium?: boolean;
}

export const LessonCard: FC<LessonCardProps> = ({
  id,
  title,
  description,
  points,
  isLocked = false,
  imageUrl,
  videoUrl,
  onStart,
  isPremium = false,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch user's subscription status
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        return null;
      }

      return data;
    },
  });

  const isPremiumUser = user?.email === 'welingtondesosa@gmail.com' || (subscription && subscription.status === 'active' && new Date(subscription.current_period_ends_at) > new Date());
  const isBlocked = isPremium && !isPremiumUser;

  const handleClick = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (isBlocked) {
      toast({
        title: "Contenido Premium",
        description: "Actualiza tu plan para acceder a todo el contenido premium.",
        variant: "destructive",
      });
      return;
    }

    if (onStart) {
      onStart();
    }
    navigate(`/lessons/${id}`);
  };

  const getPlainTextDescription = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
  };

  const pointsBreakdown = {
    video: 30,     // Video es el contenido principal
    audio: 20,     // Audio complementario
    reading: 10,   // Lectura complementaria
    quiz: 40       // Quiz (4 puntos por cada respuesta correcta, 10 preguntas)
  };

  return (
    <Card 
      className={`overflow-hidden transition-all duration-300 hover:shadow-lg ${
        isPremium ? 'border-amber-500/50' : ''
      } ${isBlocked ? 'opacity-90 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={handleClick}
    >
      <LessonCardHeader
        title={title}
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        isLocked={isBlocked}
      />
      <CardContent className="p-6">
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
              {isPremium ? (
                <Badge variant="premium" className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white">
                  Premium
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-gradient-to-r from-green-400 to-green-600 text-white">
                  Free
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600">{getPlainTextDescription(description)}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Video className="w-4 h-4" />
              <span>{pointsBreakdown.video} pts</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Headphones className="w-4 h-4" />
              <span>{pointsBreakdown.audio} pts</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <BookOpen className="w-4 h-4" />
              <span>{pointsBreakdown.reading} pts</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Trophy className="w-4 h-4" />
              <span>{pointsBreakdown.quiz} pts</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium">{points} puntos totales</span>
            </div>
            <Button
              onClick={handleClick}
              variant={isBlocked ? "outline" : "default"}
              className={`${
                isBlocked 
                  ? 'bg-gray-100 text-gray-500 hover:bg-gray-100 cursor-not-allowed' 
                  : 'bg-primary hover:bg-primary/90'
              }`}
              disabled={isBlocked}
            >
              {isBlocked ? 'Contenido Premium' : 'Comenzar'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};